import { useState, useCallback, useEffect, Fragment, useRef } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../stores";
import { setDetailField } from "../../stores/termsTemplatesList";
import {
  changeField,
  changeResult,
  selectTermTemplatesAction,
  updateTermTemplatesAction,
} from "../../stores/termsTemplates";
import {
  ResponseFailModal,
  ResponseSuccessModal,
} from "../../components/Modal/Response";

import { TermTemplatesDetailContainerProps } from "../../types/termTemplates";
import TermTemplatesDetail from "../../components/TermsTemplates/TermTemplatesDetail";
import TermTemplatesDeleteContainer from "./TermTemplatesDeleteContainer";
import ReactQuill from "react-quill";

const TermTemplatesDetailContainer = ({
  isOpen,
  listTermTemplates,
  handleViewChange,
}: TermTemplatesDetailContainerProps) => {
  const editorRef = useRef<ReactQuill>(null);

  const dispatch = useDispatch();
  const {
    termTemplatesItem,
    termTemplatesUpdateSuccess,
    termTemplatesUpdateError,
    detailField,
    userRole,
  } = useSelector(
    ({ termTemplates, termTemplatesList, header }: RootState) => ({
      termTemplatesItem: termTemplates.termTemplates,
      termTemplatesUpdateSuccess: termTemplates.termTemplatesUpdateSuccess,
      termTemplatesUpdateError: termTemplates.termTemplatesUpdateError,
      detailField: termTemplatesList.detailField,
      userRole: header.userRole,
    }),
  );

  const [isOpenTermTemplatesDeleteModal, setIsOpenTermTemplatesDeleteModal] =
    useState<boolean>(false);
  const [
    isOpenTermTemplatesUpdateSuccessModal,
    setIsOpenTermTemplatesUpdateSuccessModal,
  ] = useState<boolean>(false);
  const [
    isOpenTermTemplatesUpdateFailModal,
    setIsOpenTermTemplatesUpdateFailModal,
  ] = useState<boolean>(false);
  const [
    isOpenRequiredParameterFailModal,
    setIsOpenRequiredParameterFailModal,
  ] = useState<boolean>(false);
  const [messageTermTemplatesUpdateFail, setMessageTermTemplatesUpdateFail] =
    useState<string>("");

  const handleSelectTermTemplates = useCallback(
    (targetId: number) => {
      if (targetId) {
        dispatch(selectTermTemplatesAction({ id: targetId }));
      }
    },
    [dispatch],
  );

  // 인풋 변경 이벤트 핸들러
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | string) => {
      if (typeof e === "string") {
        dispatch(changeField({ key: "content", value: e }));
      } else {
        const { value, name } = e.target;
        dispatch(changeField({ key: name, value }));
      }
    },
    [dispatch],
  );

  const dateFormatter = (date: Date) => {
    return moment(date).format("YYYY-MM-DD HH:mm");
  };

  const handleTermTemplatesDeleteModal = () => {
    setIsOpenTermTemplatesDeleteModal(true);
  };

  const handleGoBack = () => {
    sessionStorage.removeItem("TERM_LIST_DETAIL_ID"); // 세션 정리
    listTermTemplates();
    handleViewChange("termsTemplates", "list");
    dispatch(
      changeResult({
        key: "termTemplatesUpdateError",
        value: null,
      }),
    );
  };

  const reload = () => {
    listTermTemplates();
    handleViewChange("termTemplates", "list");
  };

  const handleUpdateTermTemplates = useCallback(() => {
    if (
      !termTemplatesItem.title ||
      !termTemplatesItem.content ||
      !termTemplatesItem.language
    ) {
      setIsOpenRequiredParameterFailModal(true);
      return;
    }
    dispatch(
      updateTermTemplatesAction({ ...termTemplatesItem, id: detailField.id }),
    );
  }, [detailField.id, termTemplatesItem, dispatch]);

  useEffect(() => {
    if (termTemplatesUpdateError) {
      if (!termTemplatesUpdateError.response) {
        setMessageTermTemplatesUpdateFail(termTemplatesUpdateError.message);
        setIsOpenTermTemplatesUpdateFailModal(true);
      } else {
        if (termTemplatesUpdateError.response.data === undefined) {
          setMessageTermTemplatesUpdateFail(termTemplatesUpdateError.message);
        } else {
          setMessageTermTemplatesUpdateFail(
            `${termTemplatesUpdateError.response.data.code}, ${termTemplatesUpdateError.response.data.message}`,
          );
          if (
            termTemplatesUpdateError.response.data?.code === 401 ||
            termTemplatesUpdateError.response.data?.code === 419
          )
            reload();
        }
        setIsOpenTermTemplatesUpdateFailModal(true);
      }
    }
    if (termTemplatesUpdateSuccess) {
      setIsOpenTermTemplatesUpdateSuccessModal(true);
      setTimeout(() => {
        setIsOpenTermTemplatesUpdateSuccessModal(false);
        // 저장 성공 후 현재 ID로 다시 조회
        handleSelectTermTemplates(detailField.id);
        dispatch(
          changeResult({
            key: "termTemplatesUpdateSuccess",
            value: false,
          }),
        );
      }, 1500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    termTemplatesUpdateSuccess,
    termTemplatesUpdateError,
    handleSelectTermTemplates,
    dispatch,
  ]); // detailField.id 의존성 제거됨

  useEffect(() => {
    if (isOpen) {
      let currentId = detailField.id;

      if (!currentId) {
        const savedId = sessionStorage.getItem("TERM_LIST_DETAIL_ID");
        currentId = savedId ? Number(savedId) : 0;

        if (currentId) {
          dispatch(setDetailField(currentId));
        } else {
          return;
        }
      } else {
        sessionStorage.setItem("TERM_LIST_DETAIL_ID", String(currentId));
      }
      handleSelectTermTemplates(currentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, dispatch, handleSelectTermTemplates]);

  return (
    <Fragment>
      <TermTemplatesDetail
        termTemplatesItem={termTemplatesItem}
        userRole={userRole}
        editorRef={editorRef}
        dateFormatter={dateFormatter}
        handleChange={handleChange}
        handleUpdateTermTemplates={handleUpdateTermTemplates}
        handleTermTemplatesDeleteModal={handleTermTemplatesDeleteModal}
        handleGoBack={handleGoBack}
      />
      <TermTemplatesDeleteContainer
        isOpen={isOpenTermTemplatesDeleteModal}
        toggle={() =>
          setIsOpenTermTemplatesDeleteModal(!isOpenTermTemplatesDeleteModal)
        }
        reload={reload}
        id={detailField.id}
        termTemplatesListName={termTemplatesItem.title || ""}
      />

      <ResponseSuccessModal
        isOpen={isOpenTermTemplatesUpdateSuccessModal}
        toggle={() =>
          setIsOpenTermTemplatesUpdateSuccessModal(
            !isOpenTermTemplatesUpdateSuccessModal,
          )
        }
        message="약관 정보 수정이 완료 되었습니다."
      />
      <ResponseFailModal
        isOpen={isOpenTermTemplatesUpdateFailModal}
        toggle={() =>
          setIsOpenTermTemplatesUpdateFailModal(
            !isOpenTermTemplatesUpdateFailModal,
          )
        }
        message={
          messageTermTemplatesUpdateFail || "약관 정보 수정에 실패 하였습니다."
        }
      />
      <ResponseFailModal
        isOpen={isOpenRequiredParameterFailModal}
        toggle={() =>
          setIsOpenRequiredParameterFailModal(!isOpenRequiredParameterFailModal)
        }
        message="필수 입력값을 확인 해주세요."
      />
    </Fragment>
  );
};

export default TermTemplatesDetailContainer;
